/**
 * external dependencies
 */
import { TabulatorFull, Options, CellComponent } from 'tabulator-tables';

/**
 * internal dependencies
 */
import { showToast } from '../toasts';
import { INewPartner } from '../../models/partner.interface';
import { tableSearchInput } from './table-helpers';
import useAuth from '../../hooks/auth-hooks';

import GetPartners from '../../routes/get-partners';
import UpdateNewPartner from '../../routes/update-partner';

/**
 * Get a list of partners from the api and send the data to a function
 */
export const partnerListTable = async () => {
  const partnerTable = document.querySelector('.aafp_partner-admin-list') as HTMLElement;
  if(!partnerTable) return;

  const { body } =  await GetPartners();

  displayPartnerTable( body, partnerTable );
}

/**
 * Displays a list of Partners on the Partners page
 * 
 * @param {object[]} data the api response data
 * @param {HTMLElement} element the HTML element to display the table in
 * 
 * @returns void
 */
const displayPartnerTable = (data: INewPartner[], element: HTMLElement) => {
  const { accessType } = useAuth()!;
  element.innerHTML = '';

  function isRowSelected(cell: any){
    return cell.getRow().isSelected();
  }

  function edit_cellclick(e: any, cell: CellComponent) {
    let currentRow = cell.getRow();
    let currentTable = cell.getTable();
    let selectedRows = currentTable.getSelectedData();

    if(selectedRows.length == 0) {
      for (let i = 0; i < selectedRows.length; i++) {
        console.log(selectedRows[i]);
        selectedRows[i].deselect();
        selectedRows[i].reformat();
      }

      currentTable.deselectRow();
      currentRow.select();
      currentRow.reformat();

      let cells = currentRow.getCells();

      for (let i = 0; i < cells.length; i++) {
        cells[i].setValue(cells[i].getValue());
      }

      currentTable.hideColumn("EditButton");
      currentTable.showColumn("SaveButton");
      //currentTable.showColumn("DeleteButton");
    } 
  }

  const save_cellclick = async (e: any, cell: CellComponent) => {
    //if (!cell.getRow().isSelected()) return;

    // Save data to AWS here
    //console.log(cell.getData());
    const { partnerID, partnerName, accountNumber, exportTo, exportFileType } = cell.getData() as INewPartner;

    const partnerPath = partnerName.replace(/[^A-Za-z ]/g, '');
    const awsData = {
      partnerId: partnerID,
      partnerName,
      accountNumber,
      exportTo: partnerPath,
      exportFileType
    }

    const { body } =  await UpdateNewPartner(awsData);
    e.preventDefault();

    // setTimeout(() => {
    //   console.log(body);
    // }, 4500);

    showToast('aafp_toast',`${partnerName} data updated`);
    stopEditing(cell);
  } 

  function delete_cellclick(e: any, cell: CellComponent){
    if (!cell.getRow().isSelected()) return '';

    let data = cell.getData() as INewPartner;

    // Toast alert here
    if(window.confirm("Delete the user "+data.partnerName+"?")) {
      stopEditing(cell);
      // Delete data in AWS here
      //console.log(cell.getData());
      cell.getRow().delete();
    }
  }

  function stopEditing(cell: any){
    let currentRow = cell.getRow();
    partnerTableGrid.deselectRow();
    partnerTableGrid.showColumn("EditButton");
    //partnerTableGrid.hideColumn("DeleteButton");
    partnerTableGrid.hideColumn("SaveButton");
    currentRow.reformat()
  }

  function edit_button(cell: CellComponent, formatterParams: any) {
    const data = cell.getData() as INewPartner;
    const userPrev = accessType;

    const editButton =  `<a role="button" 
                            id="aag_edit-personnel_${data.partnerID}" 
                            class="aafp_btn-table btn_theme-primary" 
                            data-toggle="tooltip"
                            data-placement="bottom" 
                            title="Edit ${data.partnerName}">
                          <i class="fas fa-pen"></i>
                        </a>`;

    if (userPrev === 'E') {
      return editButton;
    } else {
      return '';
    }
  }

  function save_button(cell: CellComponent, formatterParams: any) {
    if (!cell.getRow().isSelected()) return '';

    const data = cell.getData() as INewPartner;

    return `<a role="button" 
                id="aag_update-personnel_${data.partnerID}" 
                class="aafp_btn-table btn_theme-outline" 
                data-toggle="tooltip"
                data-placement="bottom" 
                title="Save ${data.partnerName}">
              <i class="far fa-save"></i>
            </a>`;
  }

  function delete_button(cell: CellComponent, formatterParams: any) {
    if (!cell.getRow().isSelected()) return '';

    const data = cell.getData() as INewPartner;

    return `<a role="button" 
                id="aag_delete-personnel_${data.partnerID}" 
                class="aafp_btn-table btn_theme-warning"
                data-toggle="tooltip" 
                data-placement="bottom" 
                title="Delete ${data.partnerName}">
              <i class="far fa-trash-alt"></i>
            </a>`
  }

  const options: Options = {
    index:"ID",
    data: data,
    pagination:true,
    paginationMode: "local",
    paginationSize: 10,
    paginationSizeSelector: [25, 50, 100],
    paginationCounter: "rows",
    layoutColumnsOnNewData:true,
    minHeight: "300px",
    selectableRows: false,
    columns: [
      {title: "ID", field: "ID", visible: false},
      {title: "Partner Name", field: "partnerName", sorter: "string", cssClass: "aafp_column-", editable:isRowSelected, editor: "input", editorParams:{ elementAttributes:{ maxLength: 50 } }, resizable: false, vertAlign: 'middle'},
      {title: "Account Number", field: "accountNumber", sorter: "string", cssClass: "aafp_column-", editable:isRowSelected, editor: "number", resizable: false, vertAlign: 'middle',},
      {title: "Export File Type", field: "exportFileType", sorter: "string", cssClass: "aafp_column-4", editable:isRowSelected, vertAlign: 'middle', editor:"list", editorParams:{ values: { "CSV":"CSV", "XML":"XML" } } },
      { title: '', field: "EditButton", formatter: edit_button, cssClass: "aafp_column-button", hozAlign: 'right', width: 40, cellClick: edit_cellclick, headerSort: false, resizable: false},
      //{ title: '', field: "SaveButton", formatter: save_button, cssClass: "aafp_column-button", hozAlign: 'right', width: 40, cellClick: save_cellclick, headerSort: false, resizable: false, visible: false }
    ]
  };

  const partnerTableGrid = new TabulatorFull(element, options);
  const partnerListSort = document.querySelector('#aafp_partner-list-sort') as HTMLInputElement;
  tableSearchInput(partnerListSort, partnerTableGrid);
}